import moment from 'moment-timezone';
import { i18n } from '/plugins/i18n.client';
import { extentionList } from '../../../features/object-records/helpers/file-upload-helper';
import { numberFormat } from '/src/common/general-utils';

const autoGeneratedFields = [
  { DatabaseColumnName: 'ID', FieldType: 'DEFAULT' },
  { DatabaseColumnName: 'CreatedAt', FieldType: 'DATE' },
  { DatabaseColumnName: 'CreatedBy', FieldType: 'DEFAULT' },
  { DatabaseColumnName: 'UpdatedAt', FieldType: 'DATE' },
  { DatabaseColumnName: 'UpdatedBy', FieldType: 'DEFAULT' }
];

/**
 * transform the record to show on UI
 */
// eslint-disable-next-line max-params
export const transformFileUploadField = (DatabaseColumnName, FieldType, Settings, Label, record, keepEmptyOnInit) => {
  const recordDetails = {};

  record?.fileDetails?.[Label]?.forEach((file) => {
    const fileName = file.Filename || '';
    const fileType = fileName.split('.').pop();

    const extension = fileType?.toLowerCase();
    const mappedMimeType = extentionList[extension]?.[0] || fileType;
    const finalType = mappedMimeType || fileType;

    recordDetails[fileName] = {
      size: file.FileSize,
      type: finalType
    };
  });

  return {
    [DatabaseColumnName]: transformRecordByField(FieldType, record[DatabaseColumnName], Settings, keepEmptyOnInit),
    [`${DatabaseColumnName}_recordDetails`]: recordDetails
  };
};

export const transformRecord = (fields, record, keepEmptyOnInit) =>
  fields.concat(autoGeneratedFields).reduce((payload, { DatabaseColumnName, FieldType, Settings, Label }) => {
    if (FieldType === 'FILE_UPLOAD' && Object.values(record).length) {
      return {
        ...payload,
        ...transformFileUploadField(DatabaseColumnName, FieldType, Settings, Label, record, keepEmptyOnInit)
      };
    }

    if (FieldType === 'CURRENCY') {
      payload[`${DatabaseColumnName}Currency`] = record[`${DatabaseColumnName}Currency`];
    }

    return {
      ...payload,
      [DatabaseColumnName]: transformRecordByField(FieldType, record[DatabaseColumnName], Settings, keepEmptyOnInit)
    };
  }, {});

/**
 * Get payload for record saving
 */
export const getRecordPayload = (fields, record, getIdFields = false) => {
  let payload = {};
  if (record.UUID) {
    payload.UUID = record.UUID;
  }

  return fields.reduce((payload, { DatabaseColumnName, FieldType }) => {
    if (FieldType === 'CURRENCY') {
      payload[`${DatabaseColumnName}Currency`] = record[`${DatabaseColumnName}Currency`];
    }

    if (getIdFields && ['USER', 'ENTITY'].includes(FieldType)) {
      payload[`${DatabaseColumnName}ID`] = record[`${DatabaseColumnName}`];
    }
    return {
      ...payload,
      [DatabaseColumnName]: record[DatabaseColumnName]
        ? transformPayloadByField(FieldType, record[DatabaseColumnName])
        : record[DatabaseColumnName]
    };
  }, payload);
};

const getInteger = (recordValue) =>
  recordValue !== null && isNaN(parseInt(recordValue)) ? undefined : recordValue && parseInt(recordValue);

const getNumber = (recordValue) => (isNaN(Number(recordValue)) ? undefined : recordValue && Number(recordValue));

const getDate = (recordValue) => {
  if (recordValue === null) {
    return null;
  }
  const dateTime = new Date(recordValue);
  return !recordValue || dateTime.toString() === 'Invalid Date' ? undefined : dateTime.toISOString();
};

const getUser = (recordValue, settings) => {
  if (!settings.IsMultiSelect)
    return typeof recordValue === 'string' && settings.fieldOfReference === 'Users'
      ? parseInt(recordValue)
      : recordValue;
  /**
   * when the field is multi-select, converts string values into array
   * in case of fieldOfReference 'Users' parse value to int
   * eg. '23,R1' => ["23","R1"]
   *     '1,2' => [1,2] (for Users)
   */
  let value;
  value = (recordValue && recordValue.split(',')) || [];
  if (settings.fieldOfReference === 'Users') {
    value = value.map((item) => parseInt(item));
  }
  return value;
};

/**
 * convert record values according to field type to display in UI
 */
function transformRecordByField(fieldType, recordValue, settings, keepEmptyOnInit) {
  // Below code is to make sure that in case of bulk action the below field types does not get empty
  // these were set to empty as we were returning []
  if (
    keepEmptyOnInit &&
    recordValue === undefined &&
    ['CHECKBOX', 'TAG', 'USER', 'DROPDOWN', 'REFERENCE'].includes(fieldType)
  ) {
    return recordValue;
  }
  switch (fieldType) {
    case 'CHECKBOX': {
      /**
       * convert string from values into object with true value
       * eg. 'c1,c2' => {c1: true, c2: true}
       */
      let values = recordValue ? recordValue.split(',') : [];
      return values.reduce((model, value) => {
        model[value] = true;
        return model;
      }, {});
    }
    case 'TAG': {
      /**
       * convert string values into array
       * eg. 'tag1,tag2' => ["tag1","tag2"]
       */
      return (recordValue && recordValue.split(',')) || [];
    }
    case 'USER': {
      return getUser(recordValue, settings);
    }
    case 'DROPDOWN':
    case 'REFERENCE': {
      if (!settings.IsMultiSelect) return recordValue;
      return (recordValue && recordValue.split(',')) || [];
    }
    case 'STAR_RATING': {
      return getInteger(recordValue);
    }
    case 'NUMBER': {
      return getNumber(recordValue);
    }
    case 'DATE': {
      return getDate(recordValue);
    }
    default:
      return recordValue;
  }
}

/**
 * transform the payload on basis of field type to be saved in database
 */
function transformPayloadByField(fieldType, recordValue) {
  switch (fieldType) {
    case 'CHECKBOX': {
      /**
       * find the keys having value true. Returns null if none of the key has true value
       * eg. {c1: false, c2: true} => 'c2'
       */
      return (
        Object.keys(recordValue)
          .filter((k) => recordValue[k])
          .join(',') || null
      );
    }
    case 'TAG': {
      /**
       * convert array into string, return null in case of empty array
       * eg. ['tag1',tag2] => 'tag1,tag2
       */
      return recordValue.join(',') || null;
    }
    case 'USER':
    case 'DROPDOWN':
    case 'REFERENCE': {
      /**
       * convert multi-select array into string if value is in array format
       * eg. ['2','R2'] => '2,R2'
       */
      if (Array.isArray(recordValue)) {
        return recordValue.join(',') || null;
      }
      return recordValue;
    }
    default:
      return recordValue;
  }
}

/**
 * Preferred countries list, will be on top of the dropdown
 */
export const preferredCountriesList = ['nl', 'gb', 'de', 'us', 'au', 'be', 'sg', 'it', 'hk', 'ca', 'ke'];

const getSuggestionDatabaseValue = (suggestion) =>
  suggestion.databaseValue ??
  suggestion.DatabaseValue ??
  suggestion.value ??
  suggestion.Value ??
  suggestion.Key ??
  suggestion.key;

const getSuggestionDisplayValue = (suggestion) =>
  suggestion.displayValue ??
  suggestion.DisplayValue ??
  suggestion.text ??
  suggestion.Text ??
  suggestion.title ??
  suggestion.Title ??
  getSuggestionDatabaseValue(suggestion);

const normalizeOptionSuggestions = (suggestions) => {
  if (Array.isArray(suggestions)) return suggestions;
  if (typeof suggestions !== 'string') return [];

  try {
    const parsedSuggestions = JSON.parse(suggestions);
    return Array.isArray(parsedSuggestions) ? parsedSuggestions : [];
  } catch {
    return [];
  }
};

const getOptionDisplayValue = (field, value) => {
  const suggestions = normalizeOptionSuggestions(field.Suggestions);
  const displayByDatabaseValue = suggestions.reduce((displayValues, suggestion) => {
    const databaseValue = getSuggestionDatabaseValue(suggestion);
    if (databaseValue === null || databaseValue === undefined) return displayValues;

    return {
      ...displayValues,
      [String(databaseValue)]: i18n.global.t(getSuggestionDisplayValue(suggestion))
    };
  }, {});
  const getDisplayValue = (databaseValue) => displayByDatabaseValue[String(databaseValue)] ?? databaseValue;

  if (field.FieldType === 'CHECKBOX' && value && typeof value === 'object' && !Array.isArray(value)) {
    return Object.keys(value)
      .filter((key) => value[key] === true)
      .map(getDisplayValue)
      .join(', ');
  }
  if (Array.isArray(value)) {
    return value.map(getDisplayValue).join(', ');
  }
  return value !== null && value !== undefined ? getDisplayValue(value) : value;
};

const assignFormattedDynamicValue = (formattedValues, field, value) => {
  formattedValues[field.DatabaseColumnName] = value;
  if (field.Name) formattedValues[field.Name] = value;
  if (field.Label) formattedValues[field.Label] = value;
};

export const getFormattedDynamicValues = (fields, modifiedRecord, recordDisplayValue = {}) => {
  const dynamicValues = updateDateFieldValues(fields, modifiedRecord);
  const formattedValues = { ...recordDisplayValue };

  fields.forEach((field) => {
    const value = dynamicValues[field.DatabaseColumnName];
    let formattedValue = value;

    if (['REFERENCE', 'USER', 'ENTITY'].includes(field.FieldType)) {
      formattedValue =
        formattedValues[field.DatabaseColumnName] ??
        formattedValues[field.Name] ??
        formattedValues[field.Label] ??
        formattedValue;
    } else if (['CHECKBOX', 'RADIO', 'DROPDOWN', 'TOGGLE_SWITCH'].includes(field.FieldType)) {
      formattedValue = getOptionDisplayValue(field, modifiedRecord[field.DatabaseColumnName]);
    } else if (field.FieldType === 'FORMULA' && field.Settings?.format && value !== null && value !== undefined) {
      formattedValue = numberFormat(value, field);
    } else if (field.FieldType === 'TAG' && Array.isArray(value)) {
      formattedValue = value.join(', ');
    } else if (Array.isArray(value)) {
      formattedValue = value.join(', ');
    }

    assignFormattedDynamicValue(formattedValues, field, formattedValue);
  });

  return {
    ...dynamicValues,
    formattedValues$$: formattedValues
  };
};

// Update date field values with user's format.
export const updateDateFieldValues = (fields, modifiedRecord) =>
  fields.concat(autoGeneratedFields).reduce((payload, { DatabaseColumnName, FieldType, Settings }) => {
    let value = modifiedRecord[DatabaseColumnName];
    if (value && FieldType === 'DATE') {
      let format = moment.defaultFormat;
      if (Settings && !Settings.IsTimeInputEnable) {
        let match = format.match(/HH|hh|H|h/);
        if (match) {
          format = format.substring(0, match.index - 1);
        }
      }
      const date = moment(value).format(format);
      value = date === 'Invalid date' ? null : date;
    }
    const valueToAppend = { [DatabaseColumnName]: value };

    if (FieldType === 'CURRENCY' && modifiedRecord[DatabaseColumnName + 'Currency']) {
      valueToAppend[DatabaseColumnName + 'Currency'] = modifiedRecord[DatabaseColumnName + 'Currency'];
    }
    return {
      ...payload,
      ...valueToAppend,
      UUID: modifiedRecord.UUID
    };
  }, {});
